/**
 * Класс для `ScrollArea`: возвращает внутреннему div вьюпорта Radix блочное поведение.
 *
 * По умолчанию Radix задаёт этому div `display: table` — из-за этого содержимое растягивается
 * по самой длинной строке, а не по ширине области: сокращение и перенос текста не срабатывают,
 * и на узком экране появляется горизонтальная прокрутка вместе с раздутой карточкой.
 */
export const SCROLL_AREA_VIEWPORT_BLOCK = '[&>[data-radix-scroll-area-viewport]>div]:!block';

/**
 * Класс для `DialogContent` из kit, чьё содержимое может оказаться выше экрана.
 *
 * Своей высотой окно kit за экран не держится: оно стоит по центру и растёт в обе стороны, унося
 * за края и начало содержимого, и кнопки окна. Потолок высоты считается по видимой части экрана —
 * на телефоне её занижает адресная строка браузера. Дети окна идут в порядке: шапка, `ScrollArea`
 * (с {@link SCROLL_AREA_VIEWPORT_BLOCK}), подвал, если он есть, — не поместившееся прокручивается
 * внутри области, а шапка и подвал остаются на виду. Окно, чьё содержимое помещается, остаётся
 * по своей высоте.
 *
 * Строки под подвал в шаблоне нет намеренно: подвал встаёт следующей строкой по своей высоте, а
 * у окна без подвала заданная пустая строка добавила бы снизу лишний зазор.
 */
export const DIALOG_CONTENT_SCREEN_BOUNDED =
  'max-h-[calc(100dvh_-_2rem)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden';

/**
 * Класс для контейнера `Markdown` kit: код в разметке читается в обеих темах.
 *
 * Kit красит блок и строку кода фиксированным светлым фоном, а цвет текста берётся из темы — в тёмной
 * теме светлый текст на светлом фоне не виден. Строке кода внутри блока фон и отступ не нужны: отступ
 * сдвигал первую строку блока. Снять, когда исправят в kit.
 */
export const MARKDOWN_CODE_CLASS =
  '[&_code]:bg-muted [&_pre]:border [&_pre]:bg-muted [&_pre_code]:bg-transparent [&_pre_code]:p-0';

/**
 * Единый потолок высоты блоков ряда дашборда (рейтинги, критерии, низкие оценки, топ-5 звонков,
 * красные зоны, стабильность, тренды, инсайты) — в пикселях, для блоков, которым высота задаётся
 * значением, а не классом (`DataGrid`).
 *
 * Значение подобрано так, чтобы блок топ-5 звонков помещался целиком: пять карточек звонка
 * с датой, клиентом и обращением укладываются полностью, включая перенос строки на длинных
 * ФИО и обращениях. Прежние 30rem обрезали пятую карточку при переносе описания на вторую строку.
 */
export const DASHBOARD_BLOCK_MAX_HEIGHT = 600;

/**
 * Тот же потолок ({@link DASHBOARD_BLOCK_MAX_HEIGHT}) классом Tailwind: `max-h-150` — это 37.5rem,
 * то есть те же 600 px.
 *
 * Класс и число обязаны совпадать: иначе соседние блоки одного ряда обрываются на разной высоте.
 */
export const DASHBOARD_BLOCK_MAX_HEIGHT_CLASS = 'max-h-150';

/** Потолок блока вместе с прокруткой: не поместившееся прокручивается, не растягивая ряд. */
export const DASHBOARD_BLOCK_SCROLL_CLASS = `${DASHBOARD_BLOCK_MAX_HEIGHT_CLASS} overflow-y-auto`;

/**
 * Прочерк на месте величины, которой нет: пустое значение показывается им везде, где величина
 * выводится текстом (диаграммы, форматтеры порогов, целевых долей и прироста, итог заключения).
 */
export const EMPTY_VALUE = '—';

/**
 * Сигнальный цвет подписи деструктивного бейджа kit `outline`; кнопкам — `DESTRUCTIVE_BUTTON_CLASS`.
 *
 * Kit пишет такую подпись цветом `destructive-foreground`, а в теме проекта (и в теме с бэка) это белый —
 * пара к красной заливке, и на светлом фоне подпись пропадает. Важность нужна, чтобы перебить цвет kit.
 */
export const DESTRUCTIVE_TEXT_CLASS = 'text-destructive!';
